import { api } from "./api.ts";
import { t } from "./i18n.ts";
import type { MailCapabilities, MailCapability } from "../../shared/mailCapabilities.ts";

/**
 * "May I offer this button that needs the owner's mailbox?" - `aiStatus.ts`'s shape for the mail
 * seam (2026-08-11).
 *
 * ⚠ **The owner reported the gap as a bug, and it is the same one round 117 fixed for AI:** *"Check
 * Replies and Scan Sent Mail do not work until an email connector is set up, do mark the buttons as
 * disabled, and add an info tooltip explaining why."* Both buttons were live on a fresh install,
 * both went to the server, and both came back as a red toast reading *No mailbox is connected* -
 * which is this app's own "every knob is a promise" rule broken in the place a new owner meets
 * first. A control the install cannot honor is shown disabled WITH the reason.
 *
 * ⚠ **It asks for `readReplies`, not for "mail", and that distinction is the seam's whole point.**
 * Sending through SMTP and reading through IMAP are two connections, so an owner can have a working
 * outreach lane and no reply detection at all - and the failure of a single boolean here is
 * specific and bad: `check-replies` reports **"0 new replies"**, a sentence about the OUTLETS, when
 * the truth is that nothing looked. See `shared/mailCapabilities.ts`.
 *
 * A BOOT FACT like the AI one: `app.ts` loads it once beside `/api/meta`, so `mailWall()` is a
 * synchronous read and no island needs a loading branch. Fail-open on error for the same reason -
 * an unreachable status endpoint must never gray out a working install's buttons; the server is
 * still the enforcement, this is only the honest surface.
 */

let cache: MailCapabilities | null = null;
let inflight: Promise<MailCapabilities | null> | null = null;

export async function loadMailStatus(force = false): Promise<MailCapabilities | null> {
	if (force) {
		cache = null;
		inflight = null;
	}
	if (cache != null) return cache;
	inflight ??= api<{ capabilities: MailCapabilities }>("/api/settings/mail")
		.then(result => {
			cache = result.capabilities;
			return cache;
		})
		.catch(() => {
			// Fail open: leave the buttons alone and let the server answer for itself
			inflight = null;
			return null;
		});
	return inflight;
}

/**
 * Why this mail capability cannot be used right now, in the reader's language - or `""` when it can.
 *
 * A STRING for the same reason `aiWall` is one: a call site needs both halves at once
 * (`?disabled=${wall !== ""}` and the reason beside it). Empty while the status has not loaded, so a
 * button is never grayed on a guess.
 */
export function mailWall(capability: MailCapability): string {
	const state = cache?.[capability];
	if (state == null || state.available || state.reasonKey == null) return "";
	return t(state.reasonKey);
}
